import { el, clear } from '../lib/dom.js';
import { getBerry } from '../data/api.js';
import { loadSnapshot } from '../data/snapshot.js';
import { prettify } from '../data/pokedex-resolver.js';
import { TypeChip } from '../components/TypeChip.js';

const ITEM_SPRITE = (name) =>
  `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/items/${name}.png`;

const FLAVOR_LABEL = { spicy: 'Spicy', dry: 'Dry', sweet: 'Sweet', bitter: 'Bitter', sour: 'Sour' };

function fact(k, v) {
  return el('div', { class: 'fact' }, el('dt', {}, k), el('dd', {}, String(v)));
}

export async function BerryDetail(id) {
  const view = el('section', { class: 'view lookup' });
  view.append(el('div', { class: 'view--loading' }, 'Loading berry…'));

  await loadSnapshot();

  let d;
  try {
    d = await getBerry(id);
  } catch (err) {
    clear(view).append(
      el(
        'div',
        { class: 'view--error' },
        el('h1', {}, 'Could not load this berry'),
        el('p', {}, navigator.onLine ? err.message : 'You appear to be offline.'),
        el('a', { class: 'button', href: '#/search' }, 'Back to lookup'),
      ),
    );
    return view;
  }

  const itemName = d.item?.name || `${d.name}-berry`;
  const img = el('img', { class: 'lookup__item-img', alt: d.name, src: ITEM_SPRITE(itemName) });
  img.addEventListener('error', () => img.remove(), { once: true });

  // The five Pokéblock/Poffin flavor potencies — shown as small bars, same
  // spirit as StatBar but simple enough not to need the shared component.
  const flavors = (d.flavors || []).map((f) => ({
    name: f.flavor?.name,
    potency: f.potency || 0,
  }));
  const maxPotency = Math.max(10, ...flavors.map((f) => f.potency));

  clear(view).append(
    el('nav', { class: 'lookup__nav' }, el('a', { class: 'link', href: '#/search' }, '‹ Lookup')),
    el(
      'header',
      { class: 'lookup__head lookup__head--item' },
      img,
      el(
        'div',
        {},
        el('h1', {}, `${d.name.replace(/-/g, ' ')} berry`),
        el('span', { class: 'lookup__class' }, prettify(d.firmness?.name || 'berry')),
      ),
    ),
    el(
      'dl',
      { class: 'pfacts' },
      fact('Growth time', `${d.growth_time} h / stage`),
      fact('Max harvest', d.max_harvest),
      fact('Size', `${d.size} mm`),
      fact('Smoothness', d.smoothness),
      fact('Soil dryness', d.soil_dryness),
      d.natural_gift_power ? fact('Natural Gift power', d.natural_gift_power) : null,
    ),
    // Spread, not a bare arg: clear(view).append(...) below is the native
    // Element.append(), which stringifies a null section into a literal
    // "null" text node instead of skipping it like el() would.
    ...(d.natural_gift_type
      ? [
          el(
            'section',
            { class: 'detail__section' },
            el('h2', {}, 'Natural Gift'),
            el(
              'p',
              {},
              'Turns the move Natural Gift into a ',
              TypeChip(d.natural_gift_type.name),
              ` attack, ${d.natural_gift_power} power.`,
            ),
          ),
        ]
      : []),
    el(
      'section',
      { class: 'detail__section' },
      el('h2', {}, 'Flavor'),
      el(
        'div',
        { class: 'berry-flavors' },
        ...flavors.map((f) =>
          el(
            'div',
            { class: 'berry-flavor' },
            el('span', { class: 'berry-flavor__label' }, FLAVOR_LABEL[f.name] || prettify(f.name)),
            el(
              'span',
              { class: 'berry-flavor__bar' },
              el('span', {
                class: 'berry-flavor__fill',
                style: `width:${(f.potency / maxPotency) * 100}%`,
              }),
            ),
            el('span', { class: 'berry-flavor__num' }, f.potency),
          ),
        ),
      ),
    ),
  );
  return view;
}
